'use client';

import { useEffect, useRef } from 'react';

type NeuralNoiseProps = {
  color?: [number, number, number];
  opacity?: number;
  speed?: number;
};

type UniformLocations = Record<string, WebGLUniformLocation | null>;

export function NeuralNoise({
  color = [0.9, 0.2, 0.4],
  opacity = 0.95,
  speed = 0.001,
}: NeuralNoiseProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const lastFrameTimeRef = useRef(0);
  const glRef = useRef<WebGLRenderingContext | null>(null);
  const uniformsRef = useRef<UniformLocations | null>(null);
  const pointerRef = useRef({ x: 0, y: 0, tX: 0, tY: 0 });

  useEffect(() => {
    const canvasEl = canvasRef.current;
    if (!canvasEl) return;

    const isMobile =
      window.matchMedia('(max-width: 767px)').matches ||
      window.matchMedia('(pointer: coarse)').matches ||
      navigator.maxTouchPoints > 0;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const shouldUseMobileSafeMode = isMobile || prefersReducedMotion;

    const gl = initShader(canvasEl);
    if (!gl) return;

    glRef.current = gl;
    const uniforms = uniformsRef.current;
    if (uniforms?.u_color) gl.uniform3f(uniforms.u_color, color[0], color[1], color[2]);
    if (uniforms?.u_speed) gl.uniform1f(uniforms.u_speed, speed);

    const cleanupEvents = setupEvents(shouldUseMobileSafeMode);
    const resizeListener = () => resizeCanvas();
    window.addEventListener('resize', resizeListener);
    window.addEventListener('orientationchange', resizeListener);

    resizeCanvas();
    render();

    return () => {
      window.removeEventListener('resize', resizeListener);
      window.removeEventListener('orientationchange', resizeListener);
      cleanupEvents();

      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      glRef.current = null;
      uniformsRef.current = null;
    };

    function initShader(canvas: HTMLCanvasElement) {
      const gl = (canvas.getContext('webgl') ||
        canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;

      if (!gl) {
        console.error('WebGL not supported');
        return null;
      }

      const vsSource = `
        precision mediump float;
        varying vec2 vUv;
        attribute vec2 a_position;
        void main() {
          vUv = 0.5 * (a_position + 1.0);
          gl_Position = vec4(a_position, 0.0, 1.0);
        }
      `;

      const fsSource = `
        precision mediump float;
        varying vec2 vUv;
        uniform float u_time;
        uniform float u_ratio;
        uniform vec2 u_pointer_position;
        uniform vec3 u_color;
        uniform float u_speed;
        vec2 rotate(vec2 uv, float th) {
          return mat2(cos(th), sin(th), -sin(th), cos(th)) * uv;
        }
        float neuro_shape(vec2 uv, float t, float p) {
          vec2 sine_acc = vec2(0.0);
          vec2 res = vec2(0.0);
          float scale = 8.0;
          for (int j = 0; j < 15; j++) {
            uv = rotate(uv, 1.0);
            sine_acc = rotate(sine_acc, 1.0);
            vec2 layer = uv * scale + float(j) + sine_acc - t;
            sine_acc += sin(layer) + 2.4 * p;
            res += (0.5 + 0.5 * cos(layer)) / scale;
            scale *= 1.2;
          }
          return res.x + res.y;
        }
        void main() {
          vec2 uv = 0.5 * vUv;
          uv.x *= u_ratio;
          vec2 pointer = vUv - u_pointer_position;
          pointer.x *= u_ratio;
          float p = clamp(length(pointer), 0.0, 1.0);
          p = 0.5 * pow(1.0 - p, 2.0);
          float t = u_speed * u_time;
          vec3 col = vec3(0.0);
          float noise = neuro_shape(uv, t, p);
          noise = 1.2 * pow(noise, 3.0);
          noise += pow(noise, 10.0);
          noise = max(0.0, noise - 0.5);
          noise *= (1.0 - length(vUv - 0.5));
          col = u_color * noise;
          gl_FragColor = vec4(col, noise);
        }
      `;

      const vertexShader = createShader(gl, vsSource, gl.VERTEX_SHADER);
      const fragmentShader = createShader(gl, fsSource, gl.FRAGMENT_SHADER);
      if (!vertexShader || !fragmentShader) return null;

      const shaderProgram = createProgram(gl, vertexShader, fragmentShader);
      if (!shaderProgram) return null;

      const uniforms = getUniforms(gl, shaderProgram);
      uniformsRef.current = uniforms;

      const vertices = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]);
      const vertexBuffer = gl.createBuffer();
      if (!vertexBuffer) return null;

      gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
      gl.useProgram(shaderProgram);

      const positionLocation = gl.getAttribLocation(shaderProgram, 'a_position');
      gl.enableVertexAttribArray(positionLocation);
      gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

      return gl;
    }

    function createShader(glContext: WebGLRenderingContext, source: string, type: number) {
      const shader = glContext.createShader(type);
      if (!shader) return null;

      glContext.shaderSource(shader, source);
      glContext.compileShader(shader);

      if (!glContext.getShaderParameter(shader, glContext.COMPILE_STATUS)) {
        console.error('Shader compile error:', glContext.getShaderInfoLog(shader));
        glContext.deleteShader(shader);
        return null;
      }

      return shader;
    }

    function createProgram(glContext: WebGLRenderingContext, vs: WebGLShader, fs: WebGLShader) {
      const program = glContext.createProgram();
      if (!program) return null;

      glContext.attachShader(program, vs);
      glContext.attachShader(program, fs);
      glContext.linkProgram(program);

      if (!glContext.getProgramParameter(program, glContext.LINK_STATUS)) {
        console.error('Program link error:', glContext.getProgramInfoLog(program));
        glContext.deleteProgram(program);
        return null;
      }

      return program;
    }

    function getUniforms(glContext: WebGLRenderingContext, program: WebGLProgram) {
      const uniforms: UniformLocations = {};
      const uniformCount = glContext.getProgramParameter(program, glContext.ACTIVE_UNIFORMS);

      for (let i = 0; i < uniformCount; i++) {
        const uniform = glContext.getActiveUniform(program, i);
        if (!uniform) continue;
        uniforms[uniform.name] = glContext.getUniformLocation(program, uniform.name);
      }

      return uniforms;
    }

    function render(now = performance.now()) {
      const glContext = glRef.current;
      const uniforms = uniformsRef.current;
      const canvas = canvasRef.current;

      if (!glContext || !uniforms || !canvas) return;

      if (shouldUseMobileSafeMode) {
        const elapsed = now - lastFrameTimeRef.current;
        if (elapsed < 33) {
          animationFrameRef.current = window.requestAnimationFrame(render);
          return;
        }
        lastFrameTimeRef.current = now;
      }

      const currentTime = now;
      pointerRef.current.x += (pointerRef.current.tX - pointerRef.current.x) * 0.2;
      pointerRef.current.y += (pointerRef.current.tY - pointerRef.current.y) * 0.2;

      if (uniforms.u_time) glContext.uniform1f(uniforms.u_time, currentTime);
      if (uniforms.u_pointer_position) {
        glContext.uniform2f(
          uniforms.u_pointer_position,
          pointerRef.current.x / window.innerWidth,
          1 - pointerRef.current.y / window.innerHeight
        );
      }

      glContext.drawArrays(glContext.TRIANGLE_STRIP, 0, 4);
      animationFrameRef.current = window.requestAnimationFrame(render);
    }

    function resizeCanvas() {
      const glContext = glRef.current;
      const uniforms = uniformsRef.current;
      const canvas = canvasRef.current;

      if (!glContext || !canvas) return;

      const devicePixelRatio = shouldUseMobileSafeMode
        ? Math.min(window.devicePixelRatio || 1, 1.25)
        : Math.min(window.devicePixelRatio, 2);
      const resolutionScale = shouldUseMobileSafeMode ? 0.8 : 1;
      canvas.width = Math.max(
        1,
        Math.floor(window.innerWidth * devicePixelRatio * resolutionScale)
      );
      canvas.height = Math.max(
        1,
        Math.floor(window.innerHeight * devicePixelRatio * resolutionScale)
      );

      if (uniforms?.u_ratio) {
        glContext.uniform1f(uniforms.u_ratio, canvas.width / canvas.height);
      }

      glContext.viewport(0, 0, canvas.width, canvas.height);
    }

    function setupEvents(disablePointerEvents: boolean) {
      if (disablePointerEvents) {
        return () => undefined;
      }

      const updateMousePosition = (x: number, y: number) => {
        pointerRef.current.tX = x;
        pointerRef.current.tY = y;
      };

      const pointermove = (e: PointerEvent) => updateMousePosition(e.clientX, e.clientY);
      const touchmove = (e: TouchEvent) => {
        const touch = e.targetTouches[0];
        if (touch) updateMousePosition(touch.clientX, touch.clientY);
      };
      const click = (e: MouseEvent) => updateMousePosition(e.clientX, e.clientY);

      window.addEventListener('pointermove', pointermove);
      window.addEventListener('touchmove', touchmove, { passive: true });
      window.addEventListener('click', click);

      return () => {
        window.removeEventListener('pointermove', pointermove);
        window.removeEventListener('touchmove', touchmove);
        window.removeEventListener('click', click);
      };
    }
  }, [color, opacity, speed]);

  return (
    <canvas data-component-id="src/components/ui/neural-noise.tsx:289:4" data-component-path="src/components/ui/neural-noise.tsx" data-component-line="289" data-component-end-line="305" data-component-file="neural-noise.tsx" data-component-name="canvas" data-component-content="%7B%22elementName%22%3A%22canvas%22%7D"
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        opacity,
        zIndex: 20,
        mixBlendMode: 'screen',
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
      }}
    />
  );
}
